<template>
    <AppLayout>
        <template #icon>
            <div class="h-10 w-10 flex items-center justify-center">
                <SettingsIcon />
            </div>
        </template>
        <template #title>{{ $t("settings.title") }}</template>

        <div class="md:block hidden">
            <Tabs class="px-6 pt-[26px]" v-model="selectedTab">
                <TabsList class="grid w-full grid-cols-5">
                    <Link
                        class="flex w-full items-center justify-center"
                        :href="tab.url"
                        v-for="(tab, i) of settingTabs"
                        :key="tab.id"
                    >
                        <TabsTrigger
                            :class="
                                cn(
                                    i === 0 ? 'rounded-l-lg' : '',
                                    i + 1 === settingTabs.length
                                        ? 'rounded-r-lg'
                                        : '',

                                    'gap-2 w-full',
                                )
                            "
                            :value="tab.id"
                            as="div"
                        >
                            <component
                                :size="20"
                                :class="[
                                    selectedTab === tab.id
                                        ? 'text-z-primary-600'
                                        : 'text-iron-500',
                                ]"
                                stroke-width="2.25"
                                :is="tab.icon"
                            />

                            {{ tab.title }}
                        </TabsTrigger>
                    </Link>
                </TabsList>
            </Tabs>
        </div>

        <div class="mt-4 px-4 md:hidden">
            <Select
                :model-value="selectedTab"
                @update:model-value="selectedTabChanged"
            >
                <SelectTrigger class="w-full">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup>
                        <SelectItem
                            v-for="tab of settingTabs"
                            :key="tab.id"
                            :value="tab.id"
                        >
                            {{ tab.title }}
                        </SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>
        <slot />
    </AppLayout>
</template>

<script lang="ts" setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import SettingsIcon from "./components/SettingsIcon.vue";

import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs-v2";
import { CreditCard, Globe, Puzzle, User, Users } from "lucide-vue-next";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Link, router } from "@inertiajs/vue3";

import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const { t: $t } = useI18n();

interface TabDetails {
    id: string;
    icon: typeof CreditCard;
    title: string;
    url: string;
}

const settingTabs: TabDetails[] = [
    {
        id: "account",
        icon: User,
        title: $t("settings.account_details"),
        url: "/user/profile",
    },
    {
        id: "teams",
        icon: Users,
        title: $t("settings.teams"),
        url: "/teams",
    },
    {
        id: "billing",
        icon: CreditCard,
        title: $t("settings.billing"),
        url: "/billing",
    },
    {
        id: "integrations",
        icon: Puzzle,
        title: $t("settings.integrations"),
        url: "/integrations",
    },
    {
        id: "domains",
        icon: Globe,
        title: $t("settings.domains"),
        url: "/domains",
    },
];

const selectedTab = ref("account");

function getCurrentlySelectedTabBasedOnRoute() {
    for (const tab of settingTabs) {
        if (tab.url === window.location.pathname) {
            selectedTab.value = tab.id;
            return;
        }
    }

    // Hack to show team billing (soon to be removed)
    if (
        window.location.pathname.includes("teams") &&
        window.location.pathname.includes("billing")
    ) {
        selectedTab.value = "billing";
    }
}

function selectedTabChanged(tabId: string) {
    const tab = settingTabs.find((t) => t.id === tabId);

    if (tab) {
        redirectTo(tab.url);
    }
}

function redirectTo(url: string) {
    console.log(url);
    router.visit(url);
}

onMounted(() => {
    getCurrentlySelectedTabBasedOnRoute();
});
</script>
